import React, { useEffect } from 'preact/compat';
import { Formik, Form, useFormikContext, FormikHelpers } from 'formik';
import { SelfServiceFormValues, validationSchema, createCase, attachFile } from '../../../lib/enquiry';
import { SelfServiceFormContext, useSelfServiceFormContext } from './context';
import { SelfServiceFormProps } from './types';
import EnquiryFields from './enquiry';
import ContactFields from './contact';
import BasicFields from './basic';
import AddressSearchFields from './address-search';
import LegalFields from './legal';
import Submit from './submit';
import { useDialog } from '../../overlay/hooks';
import Dialog from '../../overlay/dialog';
import RecaptchaField from '../recaptcha';
import FormError, { ScrollToFirstError } from '../error';
import LoadingOverlay from '../loading';

async function handleSubmit(
    { attachments, ...values }: SelfServiceFormValues,
    { setStatus, resetForm }: FormikHelpers<SelfServiceFormValues>
) {
    try {
        const caseId = await createCase(values);

        if (values.enquiryType === 'productProblem') {
            await Promise.all(attachments.map(file => {
                return attachFile(caseId, file);
            }));
        }

        resetForm();
        setStatus(true);
    } catch (error) {
        console.error(error);

        setStatus(false);
    }
}

export default function SelfServiceForm(props: SelfServiceFormProps) {
    const initialValues: SelfServiceFormValues = {
        origin: props.origin,
        attachments: [],
        firstName: '',
        lastName: '',
        email: {
            first: '',
            second: '',
        },
        address: {
            manual: false,
            search: '',
            searchComplete: false,
            line1: '',
            line2: '',
            city: '',
            county: '',
            postcode: '',
        },
        enquiryType: '',
        productProblem: {
            product: '',
            batchCode: '',
            problem: '',
        },
        productEnquiry: {
            product: '',
            enquiry: '',
        },
        telephone: '',
        agreePrivacy: false,
        recaptcha: null,
    };

    return (
        <SelfServiceFormContext.Provider value={props}>
            <Formik
                initialValues={initialValues}
                initialStatus={null}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}>
                <Form noValidate={true} id='contact-form' className='form'>
                    <ScrollToFirstError />
                    <EnquiryFields />
                    <ContactFields>
                        <div className='form__grid'>
                            <BasicFields />
                            <AddressSearchFields />
                        </div>
                        <div className='form__legal'>
                            <LegalFields />
                            <div className='form__field'>
                                <RecaptchaField name='recaptcha' />
                                <FormError name='recaptcha' />
                            </div>
                        </div>
                        <Submit />
                    </ContactFields>
                    <LoadingOverlay />
                    <SuccessDialog />
                </Form>
            </Formik>
        </SelfServiceFormContext.Provider>
    );
}

function SuccessDialog() {
    const { status } = useFormikContext();
    const [isDialogShown, showDialog, hideDialog] = useDialog();
    const { classNames } = useSelfServiceFormContext();

    useEffect(() => {
        if (status === true) {
            showDialog();
        } else {
            hideDialog();
        }
    }, [status]);

    return (
        <Dialog titleId='success-heading' shown={isDialogShown}>
            <Dialog.Heading id='success-heading' className={classNames.heading}>
                Thank you!
            </Dialog.Heading>
            <div className='type  type--center'>
                <p>Thank you, your message has been received.</p>
                <p>Please note that emails may be filtered into your junk folder by your email service provider, so be sure to check there as well.</p>
            </div>
            <Dialog.Footer>
                <a href='/' className={classNames.button.submit}>
                    Return to home
                </a>
            </Dialog.Footer>
        </Dialog>
    );
}
